"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemesQuickVoteQueue, type UseMemesQuickVoteQueueResult } from "@/hooks/useMemesQuickVoteQueue";
import { useCallback, useRef, useState } from "react";
export type MemesQuickVoteDialogState = Pick<UseMemesQuickVoteQueueResult, "activeDrop" | "hasDiscoveryError" | "isExhausted" | "isRestartingRound" | "leftThisRoundCount" | "latestUsedAmount" | "nextDrop" | "recentAmounts" | "retryDiscovery" | "submitVote" | "skipDrop" | "uncastPower" | "unratedCount" | "votingLabel"> & {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly sessionId: number;
};
type UseMemesQuickVoteDialogControllerResult = {
  readonly closeQuickVote: () => void;
  readonly dialogState: MemesQuickVoteDialogState;
  readonly isQuickVoteOpen: boolean;
  readonly openQuickVote: () => void;
  readonly prefetchQuickVote: () => void;
  readonly quickVoteSessionId: number;
};
export const useMemesQuickVoteDialogController = () => {
  const $ = _c(34);
  const [isQuickVoteOpen, setIsQuickVoteOpen] = useState(false);
  const [quickVoteSessionId, setQuickVoteSessionId] = useState(0);
  const lastIssuedSessionIdRef = useRef(0);
  const reservedSessionIdRef = useRef(null);
  const t0 = quickVoteSessionId > 0;
  let t1;
  if ($[0] !== quickVoteSessionId || $[1] !== t0) {
    t1 = {
      enabled: t0,
      sessionId: quickVoteSessionId
    };
    $[0] = quickVoteSessionId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const quickVoteQueue = useMemesQuickVoteQueue(t1);
  const {
    retryDiscovery
  } = quickVoteQueue;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (reservedSessionIdRef.current !== null) {
        return reservedSessionIdRef.current;
      }
      const nextSessionId = lastIssuedSessionIdRef.current + 1;
      reservedSessionIdRef.current = nextSessionId;
      return nextSessionId;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const reserveSessionId = t2;
  let t3;
  if ($[4] !== quickVoteSessionId || $[5] !== retryDiscovery) {
    t3 = () => {
      if (quickVoteSessionId > 0) {
        retryDiscovery();
        return;
      }
      const sessionId = reserveSessionId();
      lastIssuedSessionIdRef.current = sessionId;
      reservedSessionIdRef.current = null;
      setQuickVoteSessionId(sessionId);
    };
    $[4] = quickVoteSessionId;
    $[5] = retryDiscovery;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const prefetchQuickVote = t3;
  let t4;
  if ($[7] !== quickVoteSessionId || $[8] !== retryDiscovery) {
    t4 = () => {
      if (quickVoteSessionId > 0) {
        retryDiscovery();
        setIsQuickVoteOpen(true);
        return;
      }
      const sessionId_0 = reservedSessionIdRef.current ?? lastIssuedSessionIdRef.current + 1;
      lastIssuedSessionIdRef.current = sessionId_0;
      reservedSessionIdRef.current = null;
      setQuickVoteSessionId(sessionId_0);
      setIsQuickVoteOpen(true);
    };
    $[7] = quickVoteSessionId;
    $[8] = retryDiscovery;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const openQuickVote = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsQuickVoteOpen(false);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const closeQuickVote = t5;
  let t6;
  if ($[11] !== isQuickVoteOpen || $[12] !== quickVoteQueue.activeDrop || $[13] !== quickVoteQueue.hasDiscoveryError || $[14] !== quickVoteQueue.isExhausted || $[15] !== quickVoteQueue.isRestartingRound || $[16] !== quickVoteQueue.latestUsedAmount || $[17] !== quickVoteQueue.leftThisRoundCount || $[18] !== quickVoteQueue.nextDrop || $[19] !== quickVoteQueue.recentAmounts || $[20] !== quickVoteQueue.retryDiscovery || $[21] !== quickVoteQueue.skipDrop || $[22] !== quickVoteQueue.submitVote || $[23] !== quickVoteQueue.uncastPower || $[24] !== quickVoteQueue.unratedCount || $[25] !== quickVoteQueue.votingLabel || $[26] !== quickVoteSessionId) {
    t6 = {
      activeDrop: quickVoteQueue.activeDrop,
      hasDiscoveryError: quickVoteQueue.hasDiscoveryError,
      isExhausted: quickVoteQueue.isExhausted,
      isRestartingRound: quickVoteQueue.isRestartingRound,
      isOpen: isQuickVoteOpen,
      leftThisRoundCount: quickVoteQueue.leftThisRoundCount,
      latestUsedAmount: quickVoteQueue.latestUsedAmount,
      nextDrop: quickVoteQueue.nextDrop,
      onClose: closeQuickVote,
      recentAmounts: quickVoteQueue.recentAmounts,
      retryDiscovery: quickVoteQueue.retryDiscovery,
      sessionId: quickVoteSessionId,
      skipDrop: quickVoteQueue.skipDrop,
      submitVote: quickVoteQueue.submitVote,
      uncastPower: quickVoteQueue.uncastPower,
      unratedCount: quickVoteQueue.unratedCount,
      votingLabel: quickVoteQueue.votingLabel
    };
    $[11] = isQuickVoteOpen;
    $[12] = quickVoteQueue.activeDrop;
    $[13] = quickVoteQueue.hasDiscoveryError;
    $[14] = quickVoteQueue.isExhausted;
    $[15] = quickVoteQueue.isRestartingRound;
    $[16] = quickVoteQueue.latestUsedAmount;
    $[17] = quickVoteQueue.leftThisRoundCount;
    $[18] = quickVoteQueue.nextDrop;
    $[19] = quickVoteQueue.recentAmounts;
    $[20] = quickVoteQueue.retryDiscovery;
    $[21] = quickVoteQueue.skipDrop;
    $[22] = quickVoteQueue.submitVote;
    $[23] = quickVoteQueue.uncastPower;
    $[24] = quickVoteQueue.unratedCount;
    $[25] = quickVoteQueue.votingLabel;
    $[26] = quickVoteSessionId;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  let t7;
  if ($[28] !== isQuickVoteOpen || $[29] !== openQuickVote || $[30] !== prefetchQuickVote || $[31] !== quickVoteSessionId || $[32] !== t6) {
    t7 = {
      closeQuickVote,
      dialogState: t6,
      isQuickVoteOpen,
      openQuickVote,
      prefetchQuickVote,
      quickVoteSessionId
    };
    $[28] = isQuickVoteOpen;
    $[29] = openQuickVote;
    $[30] = prefetchQuickVote;
    $[31] = quickVoteSessionId;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
};